
import React, { useState, useRef, useCallback, useEffect } from "react";
import {
    GoogleMap,
    Marker,
    Polyline,
    Polygon,
    useJsApiLoader,
} from "@react-google-maps/api";
import axios from "axios";
import { hasJWTToken } from "../Account/RouteGuard";
import SingleSelectDropdown from "../DropDown/SingleSelectDropdown";
import MultiSelectDropdown from "../DropDown/MultiSelectDropdown";

import { Navigate } from "react-router-dom";

const THEME_COLOR = "#99cc22";
const ROBOT_COLOR = "#99cc22";

const DEFAULTS = {
    base: { lat: 18.6993144, lng: 73.9193761 },
    rowLengthM: 6.5,
    spacingM: 1.8288,
    rowCount: 5,
    bearing: 0,
    mapType: "satellite",
};

const containerStyle = { width: "100%", height: "100vh" };

// WGS84 Radius for high-precision calculation
const EARTH_RADIUS = 6378137;

// --- Helpers ---

// 9 decimal places ensures <1mm precision to handle 1cm requirements safely
const fixCoord = (n) => Number(Number(n).toFixed(9));

function destinationPoint(latDeg, lonDeg, distanceM, bearingDeg) {
    if (
        typeof window !== "undefined" &&
        window.google &&
        window.google.maps &&
        window.google.maps.geometry &&
        window.google.maps.geometry.spherical
    ) {
        const p = window.google.maps.geometry.spherical.computeOffset(
            new window.google.maps.LatLng(latDeg, lonDeg),
            distanceM,
            bearingDeg
        );
        return { lat: p.lat(), lng: p.lng() };
    }

    const lat1 = (latDeg * Math.PI) / 180;
    const lon1 = (lonDeg * Math.PI) / 180;
    const brng = (bearingDeg * Math.PI) / 180;
    const dr = distanceM / EARTH_RADIUS;

    const lat2 = Math.asin(
        Math.sin(lat1) * Math.cos(dr) +
        Math.cos(lat1) * Math.sin(dr) * Math.cos(brng)
    );

    const lon2 =
        lon1 +
        Math.atan2(
            Math.sin(brng) * Math.sin(dr) * Math.cos(lat1),
            Math.cos(dr) - Math.sin(lat1) * Math.sin(lat2)
        );

    return { lat: (lat2 * 180) / Math.PI, lng: (lon2 * 180) / Math.PI };
}

function pointInPolygonFallback(point, vs) {
    const x = point.lng,
        y = point.lat;
    let inside = false;
    for (let i = 0, j = vs.length - 1; i < vs.length; j = i++) {
        const xi = vs[i].lng,
            yi = vs[i].lat;
        const xj = vs[j].lng,
            yj = vs[j].lat;
        const intersect =
            yi > y !== yj > y &&
            x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
        if (intersect) inside = !inside;
    }
    return inside;
}

function perpendicularDistanceMeters(A, B, C) {
    if (
        typeof window !== "undefined" &&
        window.google &&
        window.google.maps &&
        window.google.maps.geometry &&
        window.google.maps.geometry.spherical
    ) {
        const ga = new window.google.maps.LatLng(A.lat, A.lng);
        const gb = new window.google.maps.LatLng(B.lat, B.lng);
        const gc = new window.google.maps.LatLng(C.lat, C.lng);

        const d13 = window.google.maps.geometry.spherical.computeDistanceBetween(ga, gc);
        if (d13 === 0) return 0;

        const theta13 = (window.google.maps.geometry.spherical.computeHeading(ga, gc) * Math.PI) / 180;
        const theta12 = (window.google.maps.geometry.spherical.computeHeading(ga, gb) * Math.PI) / 180;

        const dxt = Math.asin(Math.sin(d13 / EARTH_RADIUS) * Math.sin(theta13 - theta12)) * EARTH_RADIUS;
        return Math.abs(dxt);
    }

    // fallback planar
    const x0 = C.lng, y0 = C.lat;
    const x1 = A.lng, y1 = A.lat;
    const x2 = B.lng, y2 = B.lat;
    const num = Math.abs((y2 - y1) * x0 - (x2 - x1) * y0 + x2 * y1 - y2 * x1);
    const den = Math.sqrt((y2 - y1) ** 2 + (x2 - x1) ** 2);
    const metersPerDegree = 111320;
    return (num / den) * metersPerDegree;
}

function distanceMeters(a, b) {
    if (
        typeof window !== "undefined" &&
        window.google &&
        window.google.maps &&
        window.google.maps.geometry &&
        window.google.maps.geometry.spherical
    ) {
        return window.google.maps.geometry.spherical.computeDistanceBetween(
            new window.google.maps.LatLng(a.lat, a.lng),
            new window.google.maps.LatLng(b.lat, b.lng)
        );
    }
    // High precision manual fallback
    const toRad = (d) => (d * Math.PI) / 180;
    const dLat = toRad(b.lat - a.lat);
    const dLon = toRad(b.lng - a.lng);
    const lat1 = toRad(a.lat);
    const lat2 = toRad(b.lat);
    const sinDlat = Math.sin(dLat / 2),
        sinDlon = Math.sin(dLon / 2);
    const c = 2 * Math.asin(Math.sqrt(sinDlat * sinDlat + Math.cos(lat1) * Math.cos(lat2) * sinDlon * sinDlon));
    return EARTH_RADIUS * c;
}

function buildRobotPathFromRows(rows, opts = { zigzag: true }) {
    const path = [];
    if (!rows || rows.length === 0) return path;

    for (let i = 0; i < rows.length; i++) {
        const r = rows[i];
        if (opts.zigzag && i % 2 === 1) {
            path.push({ lat: r.end.lat, lng: r.end.lng });
            path.push({ lat: r.start.lat, lng: r.start.lng });
        } else {
            path.push({ lat: r.start.lat, lng: r.start.lng });
            path.push({ lat: r.end.lat, lng: r.end.lng });
        }
    }
    return path;
}

// --- Main Component ---
export function FieldRowsMap({
    liveRobotPosition = null,
    liveRobotHeading = 0,
    isLiveMode = false
}) {
    const { isLoaded, loadError } = useJsApiLoader({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY || "AIzaSyDmmBMzFbZmGwc3FMR90OQNy8K3WFFK9eU",
        //googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY || "AIzaSyC16Y-R8bhKWx57M63qEGelyGB3SRfxFIU",
        libraries: ["geometry"],
    });

    const operationAreaRef = useRef([
        { lat: 18.6991, lng: 73.9190 },
        { lat: 18.6991, lng: 73.9200 },
        { lat: 18.6996, lng: 73.9200 },
        { lat: 18.6996, lng: 73.9190 },

        //{ lat: 18.6994084, lng: 73.9192976 },
        //{ lat: 18.6994084, lng: 73.9193862 },
        //{ lat: 18.6992773, lng: 73.9193862 },
        //{ lat: 18.6992773, lng: 73.9192842 },
    ]);

    const [opAreaVersion, setOpAreaVersion] = useState(0);
    const [base, setBase] = useState(DEFAULTS.base);
    const [rowCount, setRowCount] = useState(DEFAULTS.rowCount);
    const [spacingMeters, setSpacingMeters] = useState(DEFAULTS.spacingM);
    const [rowLengthMeters, setRowLengthMeters] = useState(DEFAULTS.rowLengthM);
    const [bearing, setBearing] = useState(DEFAULTS.bearing);
    const [mapType, setMapType] = useState(DEFAULTS.mapType);
    const [rows, setRows] = useState([]);
    const [editingRowId, setEditingRowId] = useState(null);

    // Areas
    const [fieldAreaSqM, setFieldAreaSqM] = useState(0);
    const [totalRowAreaSqM, setTotalRowAreaSqM] = useState(0);
    const [selectedRowAreaSqM, setSelectedRowAreaSqM] = useState(0);
    const [selectedRowLengthM, setSelectedRowLengthM] = useState(0);

    // Robot state
    const [robotPath, setRobotPath] = useState([]);
    const [robotPos, setRobotPos] = useState(null);
    const [robotRunning, setRobotRunning] = useState(false);
    const [robotPaused, setRobotPaused] = useState(false);
    const [robotSpeed, setRobotSpeed] = useState(1.0);
    const [robotIndex, setRobotIndex] = useState(0);
    const [robotProgressAlongSegment, setRobotProgressAlongSegment] = useState(0);

    const robotTickerRef = useRef(null);
    const mapRef = useRef(null);
    const onLoad = useCallback((map) => (mapRef.current = map), []);

    // DropDown
    const [plantId, setPlantId] = useState("");
    const [blockId, setBlockId] = useState("");
    const [blockIds, setBlockIds] = useState([]);
    // LIVE MODE UPDATER
    useEffect(() => {
        if (isLiveMode && liveRobotPosition && liveRobotPosition.lat && liveRobotPosition.lng) {
            // Stop internal simulation if active
            if (robotRunning) {
                setRobotRunning(false);
                if (robotTickerRef.current) {
                    clearInterval(robotTickerRef.current);
                    robotTickerRef.current = null;
                }
            }
            // Update position with high precision
            setRobotPos({
                lat: fixCoord(liveRobotPosition.lat),
                lng: fixCoord(liveRobotPosition.lng)
            });
        }
    }, [isLiveMode, liveRobotPosition, robotRunning]);

    const getIcons = () => {
        if (!isLoaded || typeof window === "undefined" || !window.google) {
            return { robotIcon: null, destIcon: null, arrowSymbol: null };
        }
        const robotIcon = {
            path: "M12 2C8.13 2 5 5.13 5 9v4H3v6h4v4h8v-4h4v-6h-2V9c0-3.87-3.13-7-7-7zM9 20H7v-2H9v2zm8 0h-2v-2h2v2z",
            fillColor: isLiveMode ? "#ef4444" : ROBOT_COLOR,
            fillOpacity: 1,
            strokeWeight: 0,
            scale: 1.4,
            anchor: new window.google.maps.Point(12, 12),
        };
        const destIcon = {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 6,
            fillColor: THEME_COLOR,
            fillOpacity: 1,
            strokeWeight: 0,
        };
        const arrowSymbol = {
            path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
            scale: 3,
            fillColor: THEME_COLOR,
            fillOpacity: 1,
            strokeWeight: 0,
        };
        return { robotIcon, destIcon, arrowSymbol };
    };

    const { robotIcon, destIcon, arrowSymbol } = getIcons();

    const isInsideOperationArea = useCallback((pt) => {
        if (typeof window !== "undefined" && window.google && window.google.maps && window.google.maps.geometry && window.google.maps.geometry.poly) {
            const path = operationAreaRef.current.map((p) => new window.google.maps.LatLng(p.lat, p.lng));
            const poly = new window.google.maps.Polygon({ paths: path });
            return window.google.maps.geometry.poly.containsLocation(new window.google.maps.LatLng(pt.lat, pt.lng), poly);
        }
        return pointInPolygonFallback(pt, operationAreaRef.current);
    }, [operationAreaRef]);

    const onMapClick = (e) => {
        if (!isLoaded) return;
        const lat = fixCoord(e.latLng.lat());
        const lng = fixCoord(e.latLng.lng());
        const clicked = { lat, lng };
        if (!isInsideOperationArea(clicked)) {
            window.alert("Click inside operation area only.");
            return;
        }

        setBase(clicked);

        if (editingRowId) {
            setRows((prev) => {
                const updated = prev.map((r) => {
                    if (r.id !== editingRowId) return r;
                    const newStart = clicked;
                    const newEndRaw = destinationPoint(newStart.lat, newStart.lng, rowLengthMeters, bearing);
                    const newEnd = { lat: fixCoord(newEndRaw.lat), lng: fixCoord(newEndRaw.lng) };
                    return { ...r, start: newStart, end: newEnd };
                });
                return updated;
            });

            if (window.google && window.google.maps?.geometry) {
                const newEnd = destinationPoint(clicked.lat, clicked.lng, rowLengthMeters, bearing);
                const a = new window.google.maps.LatLng(clicked.lat, clicked.lng);
                const b = new window.google.maps.LatLng(newEnd.lat, newEnd.lng);
                const distance = window.google.maps.geometry.spherical.computeDistanceBetween(a, b);
                setRowLengthMeters(distance);
                setSelectedRowLengthM(distance);
            }

            if (rows.length > 0 && window.google && window.google.maps?.geometry) {
                const first = rows[0];
                const perp = perpendicularDistanceMeters(first.start, { lat: first.end.lat, lng: first.end.lng }, clicked);
                const fallback = window.google.maps.geometry.spherical.computeDistanceBetween(
                    new window.google.maps.LatLng(first.start.lat, first.start.lng),
                    new window.google.maps.LatLng(clicked.lat, clicked.lng)
                );
                setSpacingMeters(perp || fallback);
            }
        }
    };

    const handleGenerate = () => {
        if (!isInsideOperationArea(base)) {
            window.alert("Base must be inside operation area.");
            return;
        }

        const generated = [];
        if (editingRowId) {
            const start = { lat: fixCoord(base.lat), lng: fixCoord(base.lng) };
            const endRaw = destinationPoint(start.lat, start.lng, rowLengthMeters, bearing);
            const end = { lat: fixCoord(endRaw.lat), lng: fixCoord(endRaw.lng) };
            if (isInsideOperationArea(start) && isInsideOperationArea(end)) {
                generated.push({ id: Date.now() + Math.floor(Math.random() * 1000), start, end });
            }
            setRows((prev) => prev.map((r) => (r.id === editingRowId ? generated[0] : r)));
            setEditingRowId(null);
        } else {
            for (let i = 0; i < rowCount; i++) {
                const offset = i * spacingMeters;
                const startRaw = destinationPoint(base.lat, base.lng, offset, bearing + 90);
                const start = { lat: fixCoord(startRaw.lat), lng: fixCoord(startRaw.lng) };
                const endRaw = destinationPoint(start.lat, start.lng, rowLengthMeters, bearing);
                const end = { lat: fixCoord(endRaw.lat), lng: fixCoord(endRaw.lng) };
                if (!isInsideOperationArea(start) || !isInsideOperationArea(end)) continue;
                generated.push({ id: Date.now() + i + Math.floor(Math.random() * 1000), start, end });
            }
            setRows(generated);
        }

        setTimeout(() => {
            const newPath = buildRobotPathFromRows(rows.length ? rows : generated, { zigzag: true });
            setRobotPath(newPath);
        }, 50);

        if (mapRef.current && window.google && window.google.maps) {
            const bounds = new window.google.maps.LatLngBounds();
            operationAreaRef.current.forEach((p) => bounds.extend(p));
            rows.forEach((r) => bounds.extend(r.start).extend(r.end));
            generated.forEach((r) => bounds.extend(r.start).extend(r.end));
            mapRef.current.fitBounds(bounds);
        }
    };

    const handleDeleteRow = (id) => {
        setRows((prev) => prev.filter((r) => r.id !== id));
        setTimeout(() => {
            setRobotPath(buildRobotPathFromRows(rows.filter((rr) => rr.id !== id), { zigzag: true }));
        }, 50);
    };

    const handleEditRow = (row) => {
        setEditingRowId(row.id);
        setBase({ lat: fixCoord(row.start.lat), lng: fixCoord(row.start.lng) });

        if (window.google && window.google.maps?.geometry) {
            const a = new window.google.maps.LatLng(row.start.lat, row.start.lng);
            const b = new window.google.maps.LatLng(row.end.lat, row.end.lng);
            const distance = window.google.maps.geometry.spherical.computeDistanceBetween(a, b);
            setRowLengthMeters(distance);
            setSelectedRowLengthM(distance);
        }

        if (rows.length > 0 && window.google && window.google.maps?.geometry) {
            const firstRow = rows[0];
            const perp = perpendicularDistanceMeters(firstRow.start, { lat: firstRow.end.lat, lng: firstRow.end.lng }, row.start);
            if (perp) setSpacingMeters(perp);
            else {
                const A = new window.google.maps.LatLng(firstRow.start.lat, firstRow.start.lng);
                const B = new window.google.maps.LatLng(row.start.lat, row.start.lng);
                setSpacingMeters(window.google.maps.geometry.spherical.computeDistanceBetween(A, B));
            }
        }

        if (window.google && window.google.maps?.geometry) {
            const a = new window.google.maps.LatLng(row.start.lat, row.start.lng);
            const b = new window.google.maps.LatLng(row.end.lat, row.end.lng);
            const length = window.google.maps.geometry.spherical.computeDistanceBetween(a, b);
            const area = length * spacingMeters;
            setSelectedRowAreaSqM(area);
        }
    };

    //const handleSave = async () => {
    //    try {
    //        const payload = rows.map((r) => ({
    //            start_lat: r.start.lat,
    //            start_lng: r.start.lng,
    //            end_lat: r.end.lat,
    //            end_lng: r.end.lng,
    //            row_id: r.id,
    //        }));
    //        await axios.post("/api/rows/save", payload);
    //        window.alert("Saved successfully");
    //    } catch (err) {
    //        console.error(err);
    //        window.alert("Save failed");
    //    }
    //};

    const handleSave = async () => {
        try {
            const payload = rows.map((r) => ({
                //start_lat: r.start.lat,
                //start_lng: r.start.lng,
                //end_lat: r.end.lat,
                //end_lng: r.end.lng,
                //row_id: r.id,
                Mode:"Insert",
                plant_id:1,
                block_id:1,
                row_id:1,
                row_length:1,
                row_spacing:1,
                bearing:1,
                user_id:1
            }));
            await axios.post('https://localhost:7248/PlantBlockConfiguration/insert-update', payload);
            window.alert("Saved successfully");
        } catch (err) {
            console.error(err);
            window.alert("Save failed");
        }
    };

    useEffect(() => {
        if (!isLoaded || !window.google || !window.google.maps?.geometry) return;

        let polySqM = 0;
        const op = operationAreaRef.current;
        if (op && op.length > 2) {
            const path = op.map((p) => new window.google.maps.LatLng(p.lat, p.lng));
            polySqM = window.google.maps.geometry.spherical.computeArea(path);
        }
        setFieldAreaSqM(polySqM);

        let totalRowsArea = 0;
        let selRowArea = 0;
        let selRowLength = 0;

        const updatedRows = rows.map((r) => {
            let length = 0;
            if (r.start && r.end) {
                const a = new window.google.maps.LatLng(r.start.lat, r.start.lng);
                const b = new window.google.maps.LatLng(r.end.lat, r.end.lng);
                length = window.google.maps.geometry.spherical.computeDistanceBetween(a, b);
            }
            const area = length * spacingMeters;
            totalRowsArea += area;

            if (editingRowId && r.id === editingRowId) {
                selRowArea = area;
                selRowLength = length;
            }

            return { ...r, _calcLength: length, _calcAreaSqM: area };
        });

        if (!editingRowId && rows.length === 1) {
            selRowArea = updatedRows[0]._calcAreaSqM || 0;
            selRowLength = updatedRows[0]._calcLength || 0;
        }

        setTotalRowAreaSqM(totalRowsArea);
        setSelectedRowAreaSqM(selRowArea);
        setSelectedRowLengthM(selRowLength);

        setRobotPath(buildRobotPathFromRows(rows, { zigzag: true }));
    }, [isLoaded, rows, spacingMeters, opAreaVersion, editingRowId]);

    const buildSegmentsFromPath = (path) => {
        const segs = [];
        for (let i = 0; i < path.length - 1; i++) {
            const A = path[i];
            const B = path[i + 1];
            const len = distanceMeters(A, B);
            let bearingDeg = 0;
            if (typeof window !== "undefined" && window.google && window.google.maps?.geometry) {
                bearingDeg = window.google.maps.geometry.spherical.computeHeading(
                    new window.google.maps.LatLng(A.lat, A.lng),
                    new window.google.maps.LatLng(B.lat, B.lng)
                );
            } else {
                const toDeg = (r) => (r * 180) / Math.PI;
                const y = Math.sin((B.lng - A.lng) * Math.PI / 180) * Math.cos(B.lat * Math.PI / 180);
                const x =
                    Math.cos(A.lat * Math.PI / 180) * Math.sin(B.lat * Math.PI / 180) -
                    Math.sin(A.lat * Math.PI / 180) * Math.cos(B.lat * Math.PI / 180) * Math.cos((B.lng - A.lng) * Math.PI / 180);
                bearingDeg = toDeg(Math.atan2(y, x));
            }
            segs.push({ start: A, end: B, length: len, bearing: bearingDeg });
        }
        return segs;
    };

    const startRobot = (opts = { loop: false }) => {
        if (isLiveMode) {
            console.warn("Cannot start simulation in Live Mode");
            return;
        }

        if (!robotPath || robotPath.length < 2) {
            window.alert("No robot path available. Generate rows first.");
            return;
        }
        if (robotRunning) return;
        setRobotRunning(true);
        setRobotPaused(false);
        setRobotIndex(0);
        setRobotProgressAlongSegment(0);
        setRobotPos({ lat: robotPath[0].lat, lng: robotPath[0].lng });

        const segments = buildSegmentsFromPath(robotPath);
        let currentSegIndex = 0;
        let progressed = 0;
        const tickMs = 200;

        if (robotTickerRef.current) {
            clearInterval(robotTickerRef.current);
            robotTickerRef.current = null;
        }

        robotTickerRef.current = setInterval(() => {
            if (robotPaused || !segments.length) return;

            const seg = segments[currentSegIndex];
            const moveM = robotSpeed * (tickMs / 1000.0);
            progressed += moveM;
            if (progressed >= seg.length) {
                const arrivedPos = { lat: seg.end.lat, lng: seg.end.lng };
                setRobotPos(arrivedPos);
                currentSegIndex += 1;
                progressed = 0;
                if (currentSegIndex >= segments.length) {
                    clearInterval(robotTickerRef.current);
                    robotTickerRef.current = null;
                    setRobotRunning(false);
                    setRobotIndex(segments.length - 1);
                    setRobotProgressAlongSegment(seg.length);
                    if (opts.loop) setTimeout(() => startRobot(opts), 500);
                    return;
                } else {
                    setRobotIndex(currentSegIndex);
                    setRobotProgressAlongSegment(0);
                    setRobotPos({ lat: segments[currentSegIndex].start.lat, lng: segments[currentSegIndex].start.lng });
                }
            } else {
                const posLatLng = destinationPoint(seg.start.lat, seg.start.lng, progressed, seg.bearing);
                const pos = { lat: fixCoord(posLatLng.lat), lng: fixCoord(posLatLng.lng) };
                setRobotPos(pos);
                setRobotIndex(currentSegIndex);
                setRobotProgressAlongSegment(progressed);
            }
        }, tickMs);
    };

    const pauseRobot = () => {
        if (!robotRunning) return;
        setRobotPaused(true);
    };

    const resumeRobot = () => {
        if (isLiveMode) return;
        if (!robotRunning) return;
        setRobotPaused(false);
    };

    const stopRobot = () => {
        setRobotRunning(false);
        setRobotPaused(false);
        if (robotTickerRef.current) {
            clearInterval(robotTickerRef.current);
            robotTickerRef.current = null;
        }
        setRobotIndex(0);
        setRobotProgressAlongSegment(0);
        setRobotPos(robotPath && robotPath[0] ? { lat: robotPath[0].lat, lng: robotPath[0].lng } : null);
    };

    // cleanup
    useEffect(() => {
        return () => {
            if (robotTickerRef.current) {
                clearInterval(robotTickerRef.current);
                robotTickerRef.current = null;
            }
        };
    }, []);

    // robot path reset
    useEffect(() => {
        if (isLiveMode) return;
        if (robotPath && robotPath.length > 0) {
            setRobotPos({ lat: robotPath[0].lat, lng: robotPath[0].lng });
            setRobotIndex(0);
            setRobotProgressAlongSegment(0);
        } else {
            setRobotPos(null);
        }
    }, [robotPath, isLiveMode]);

    // bounds
    useEffect(() => {
        if (!isLoaded || !mapRef.current) return;
        const bounds = new window.google.maps.LatLngBounds();
        operationAreaRef.current.forEach((p) => bounds.extend(p));
        mapRef.current.fitBounds(bounds);
    }, [isLoaded]);

    if (loadError) return <div>Map load error</div>;
    if (!isLoaded) return <div>Loading map...</div>;
    if (!hasJWTToken()) return <Navigate to="/login" replace />;

    const mapTypeSelector = (
        <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
            <label><input type="radio" checked={mapType === "satellite"} onChange={() => setMapType("satellite")} /> Satellite</label>
            <label><input type="radio" checked={mapType === "roadmap"} onChange={() => setMapType("roadmap")} /> Roadmap</label>
            <label><input type="radio" checked={mapType === "hybrid"} onChange={() => setMapType("hybrid")} /> Hybrid</label>
            <label><input type="radio" checked={mapType === "terrain"} onChange={() => setMapType("terrain")} /> Terrain</label>
        </div>
    );

    const fmt = {
        m2: (v) => `${Math.round(v).toLocaleString()} m²`,
        ha: (v) => `${(v / 10000).toFixed(3)} ha`,
        acre: (v) => `${(v / 4046.86).toFixed(3)} ac`,
        m: (v) => `${v ? v.toFixed(2) : "0.00"} m`,
    };

    const remainingMeters = (() => {
        if (!robotPath || robotPath.length < 2) return 0;
        const segments = buildSegmentsFromPath(robotPath);
        let rem = 0;
        for (let i = robotIndex; i < segments.length; i++) {
            const seg = segments[i];
            if (i === robotIndex) rem += Math.max(0, seg.length - robotProgressAlongSegment);
            else rem += seg.length;
        }
        return rem;
    })();

    const etaSeconds = robotSpeed > 0 ? remainingMeters / robotSpeed : 0;

    return (
        <div style={{ display: "flex", height: "100vh", fontFamily: "system-ui, sans-serif" }}>
            {/* Left panel */}
            <div style={{ width: 380, padding: 16, borderRight: "1px solid #e5e7eb", boxSizing: "border-box", display: "flex", flexDirection: "column" }}>
                <h2 style={{ margin: 0 }}>Farm Crop Field Configuration</h2>

                <div style={{ marginTop: 12, padding: 12, borderRadius: 8, background: "#f8fafc", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
                    <div style={{ fontSize: 13, color: "#374151", marginBottom: 6 }}>Field & Row Areas</div>

                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                        <div style={{ color: "#6b7280", fontSize: 13 }}>Field Area</div>
                        <div style={{ textAlign: "right" }}>
                            <div style={{ fontWeight: 700 }}>{fmt.ha(fieldAreaSqM)}</div>
                            <div style={{ fontSize: 12, color: "#6b7280" }}>{fmt.m2(fieldAreaSqM)} • {fmt.acre(fieldAreaSqM)}</div>
                        </div>
                    </div>

                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                        <div style={{ color: "#6b7280", fontSize: 13 }}>Selected Row Area</div>
                        <div style={{ textAlign: "right" }}>
                            <div style={{ fontWeight: 700 }}>{fmt.ha(selectedRowAreaSqM)}</div>
                            <div style={{ fontSize: 12, color: "#6b7280" }}>{fmt.m2(selectedRowAreaSqM)} • {fmt.m(selectedRowLengthM)} length</div>
                        </div>
                    </div>

                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <div style={{ color: "#6b7280", fontSize: 13 }}>Total Row Area</div>
                        <div style={{ textAlign: "right" }}>
                            <div style={{ fontWeight: 700 }}>{fmt.ha(totalRowAreaSqM)}</div>
                            <div style={{ fontSize: 12, color: "#6b7280" }}>{fmt.m2(totalRowAreaSqM)}</div>
                        </div>
                    </div>
                </div>

            {mapTypeSelector}
           <h3>Plant & Block Selection</h3>
            <div class="row">
                <div class="col-md-6 col-12">
                    <SingleSelectDropdown
                       tableName="tbl_plant"
                       idColumn="plant_id"
                       nameColumn="plant_name"
                       value={plantId}
                       onChange={setPlantId}
                />
            </div>     

                <div class="col-md-6 col-12">
                    <SingleSelectDropdown
                        tableName="tbl_plant_blocks"
                        idColumn="block_id"
                        nameColumn="block_name"
                        value={blockId}
                        onChange={setBlockId}
                    />
                </div>
            </div>
            {/*<h3>Multiple Block Selection</h3>*/}

            {/*<MultiSelectDropdown*/}
            {/*    tableName="tbl_plant_blocks"*/}
            {/*    idColumn="block_id"*/}
            {/*    nameColumn="block_name"*/}
            {/*    selectedValues={blockIds}*/}
            {/*    onChange={setBlockIds}*/}
            {/*/>*/}

                <label style={{ display: "block", marginTop: 8 }}>Base Latitude</label>
                <input type="number" step="0.000000001" value={base.lat} onChange={(e) => setBase((p) => ({ ...p, lat: parseFloat(e.target.value) || 0 }))} style={{ width: "100%", padding: 8 }} />

                <label style={{ display: "block", marginTop: 8 }}>Base Longitude</label>
                <input type="number" step="0.000000001" value={base.lng} onChange={(e) => setBase((p) => ({ ...p, lng: parseFloat(e.target.value) || 0 }))} style={{ width: "100%", padding: 8 }} />

                <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                    <div style={{ flex: 1 }}>
                        <label style={{ display: "block" }}>Rows</label>
                        <input type="number" value={rowCount} onChange={(e) => setRowCount(parseInt(e.target.value) || 0)} style={{ width: "100%", padding: 8 }} />
                    </div>
                    <div style={{ flex: 1 }}>
                        <label style={{ display: "block" }}>Bearing (°)</label>
                        <input type="number" value={bearing} onChange={(e) => setBearing(parseFloat(e.target.value) || 0)} style={{ width: "100%", padding: 8 }} />
                    </div>
                </div>

                <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                    <div style={{ flex: 1 }}>
                        <label style={{ display: "block" }}>Row Length (m)</label>
                        <input type="number" value={rowLengthMeters} onChange={(e) => setRowLengthMeters(parseFloat(e.target.value) || 0)} style={{ width: "100%", padding: 8 }} />
                    </div>

                    <div style={{ flex: 1 }}>
                        <label style={{ display: "block" }}>Spacing (m)</label>
                        <input type="number" value={spacingMeters} onChange={(e) => setSpacingMeters(parseFloat(e.target.value) || 0)} style={{ width: "100%", padding: 8 }} />
                    </div>
                </div>

                <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                    <button onClick={handleGenerate} style={{ flex: 1, padding: 10, background: THEME_COLOR, color: "white", border: "none", borderRadius: 6 }}>
                        {editingRowId ? "Update Row" : "Generate Rows"}
                    </button>
                    <button onClick={handleSave} style={{ flex: 1, padding: 10, background: THEME_COLOR, color: "white", border: "none", borderRadius: 6 }}>
                        Save
                    </button>
                </div>

                {editingRowId && (
                    <button onClick={() => setEditingRowId(null)} style={{ padding: 10, background: "#6b7280", color: "white", border: "none", borderRadius: 6, marginTop: 8 }}>
                        Cancel Edit
                    </button>
                )}

                <div style={{ marginTop: 12, padding: 12, borderRadius: 8, background: isLiveMode ? "#fee2e2" : "#ffffff", border: isLiveMode ? "1px solid #ef4444" : "none", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
                    <div style={{ fontWeight: 700, marginBottom: 8, color: isLiveMode ? "#b91c1c" : "inherit" }}>
                        {isLiveMode ? "🔴 Real-Time Navigation" : "Robot Simulation"}
                    </div>

                    {isLiveMode ? (
                        <div style={{ fontSize: 13, color: "#7f1d1d" }}>
                            <div>Coordinates (High Precision):</div>
                            <div style={{ fontFamily: "monospace", marginTop: 4 }}>
                                Lat: {liveRobotPosition?.lat ? fixCoord(liveRobotPosition.lat) : "Waiting..."}<br />
                                Lng: {liveRobotPosition?.lng ? fixCoord(liveRobotPosition.lng) : "Waiting..."}
                            </div>
                            <div style={{ marginTop: 4 }}>Heading: {liveRobotHeading ? liveRobotHeading.toFixed(1) : 0}°</div>
                        </div>
                    ) : (
                        <>
                            <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                                <button onClick={() => startRobot({ loop: false })} style={{ flex: 1, padding: 8, background: "#10b981", color: "white", border: "none", borderRadius: 6 }}>Start</button>
                                <button onClick={pauseRobot} style={{ padding: 8, background: "#f59e0b", color: "white", border: "none", borderRadius: 6 }}>Pause</button>
                                <button onClick={resumeRobot} style={{ padding: 8, background: "#3b82f6", color: "white", border: "none", borderRadius: 6 }}>Resume</button>
                                <button onClick={stopRobot} style={{ padding: 8, background: "#ef4444", color: "white", border: "none", borderRadius: 6 }}>Stop</button>
                            </div>
                            <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
                                <label style={{ minWidth: 80 }}>Speed</label>
                                <input type="range" min="0.2" max="3.0" step="0.1" value={robotSpeed} onChange={(e) => setRobotSpeed(parseFloat(e.target.value))} style={{ flex: 1 }} />
                                <div style={{ width: 60, textAlign: "right" }}>{robotSpeed.toFixed(1)} m/s</div>
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: "#374151" }}>
                                <div>Index: {robotIndex}</div>
                                <div>ETA: {Math.floor(etaSeconds / 60)}m {Math.round(etaSeconds % 60)}s</div>
                            </div>
                        </>
                    )}
                </div>

                <div style={{ marginTop: 12 }}>
                    <h4 style={{ margin: 0 }}>Generated Rows</h4>
                    <div style={{ marginTop: 8, fontSize: 13, color: "#374151" }}>
                        {rows.length === 0 ? (
                            <div style={{ color: "#6b7280" }}>No rows generated</div>
                        ) : (
                            <div>
                                {rows.map((r) => (
                                    <div key={r.id} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px dashed #e5e7eb" }}>
                                        <div>
                                            <div style={{ fontSize: 13, fontWeight: 700 }}>Row #{r.id}</div>
                                            <div style={{ fontSize: 12, color: "#6b7280" }}>
                                                {r._calcLength ? `${r._calcLength.toFixed(2)} m` : ""} {r._calcAreaSqM ? ` • ${Math.round(r._calcAreaSqM).toLocaleString()} m²` : ""}
                                            </div>
                                        </div>
                                        <div style={{ display: "flex", gap: 8 }}>
                                            <button onClick={() => handleEditRow(r)} style={{ background: "#3b82f6", color: "white", border: "none", padding: "4px 8px", borderRadius: 4 }}>Edit</button>
                                            <button onClick={() => handleDeleteRow(r.id)} style={{ background: "#ef4444", color: "white", border: "none", padding: "4px 8px", borderRadius: 4 }}>Delete</button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Map */}
            <div style={{ flex: 1 }}>
                <GoogleMap
                    mapContainerStyle={containerStyle}
                    center={base}
                    zoom={19}
                    mapTypeId={mapType}
                    onLoad={onLoad}
                    onClick={onMapClick}
                >
                    <Polygon
                        path={operationAreaRef.current}
                        options={{ fillColor: THEME_COLOR, fillOpacity: 0.12, strokeColor: THEME_COLOR, strokeWeight: 2 }}
                        editable={true}
                        draggable={true}
                        onMouseUp={(e) => {
                            const newPath = e.latLngs
                                ? e.latLngs.getArray().map((p) => ({ lat: fixCoord(p.lat()), lng: fixCoord(p.lng()) }))
                                : operationAreaRef.current;
                            operationAreaRef.current = newPath;
                            setOpAreaVersion((v) => v + 1);
                            setRows((prev) => [...prev]);
                        }}
                    />

                    {rows.map((r) => (
                        <React.Fragment key={r.id}>
                            <Polyline path={[r.start, r.end]} options={{ strokeColor: THEME_COLOR, strokeWeight: 3, icons: arrowSymbol ? [{ icon: arrowSymbol, offset: "50%" }] : [] }} />
                            {robotIcon ? (
                                <Marker
                                    position={r.start}
                                    icon={robotIcon}
                                    draggable={true}
                                    onDragEnd={(e) => {
                                        const newPos = { lat: fixCoord(e.latLng.lat()), lng: fixCoord(e.latLng.lng()) };
                                        setRows((prev) => prev.map((row) => (row.id === r.id ? { ...row, start: newPos } : row)));
                                        if (window.google && window.google.maps?.geometry) {
                                            const rowObj = rows.find((rr) => rr.id === r.id) || r;
                                            const newLength = window.google.maps.geometry.spherical.computeDistanceBetween(
                                                new window.google.maps.LatLng(newPos.lat, newPos.lng),
                                                new window.google.maps.LatLng(rowObj.end.lat, rowObj.end.lng)
                                            );
                                            setRowLengthMeters(newLength);
                                            setSelectedRowLengthM(newLength);
                                            if (rows.length > 0) {
                                                const first = rows[0];
                                                const perp = perpendicularDistanceMeters(first.start, { lat: first.end.lat, lng: first.end.lng }, newPos);
                                                const fallback = window.google.maps.geometry.spherical.computeDistanceBetween(
                                                    new window.google.maps.LatLng(first.start.lat, first.start.lng),
                                                    new window.google.maps.LatLng(newPos.lat, newPos.lng)
                                                );
                                                setSpacingMeters(perp || fallback);
                                            }
                                        }
                                    }}
                                />
                            ) : (
                                <Marker position={r.start} />
                            )}

                            {destIcon && window.google ? (
                                <>
                                    <Marker
                                        position={r.end}
                                        icon={destIcon}
                                        draggable={true}
                                        onDragEnd={(e) => {
                                            const newPos = { lat: fixCoord(e.latLng.lat()), lng: fixCoord(e.latLng.lng()) };
                                            setRows((prev) => prev.map((row) => (row.id === r.id ? { ...row, end: newPos } : row)));
                                            if (window.google && window.google.maps?.geometry) {
                                                const rowObj = rows.find((rr) => rr.id === r.id) || r;
                                                const newLength = window.google.maps.geometry.spherical.computeDistanceBetween(
                                                    new window.google.maps.LatLng(rowObj.start.lat, rowObj.start.lng),
                                                    new window.google.maps.LatLng(newPos.lat, newPos.lng)
                                                );
                                                setRowLengthMeters(newLength);
                                                setSelectedRowLengthM(newLength);
                                                const area = newLength * spacingMeters;
                                                setSelectedRowAreaSqM(area);
                                            }
                                        }}
                                    />
                                    <Marker position={r.end} />
                                </>
                            ) : (
                                <Marker position={r.end} />
                            )}
                        </React.Fragment>
                    ))}

                    {robotPath && robotPath.length > 1 && (
                        <Polyline path={robotPath} options={{ strokeColor: "#2563eb", strokeWeight: 2, strokeOpacity: 0.9, zIndex: 100 }} />
                    )}

                    {robotPos && robotIcon && (
                        <Marker
                            position={robotPos}
                            icon={{
                                ...robotIcon,
                                rotation: isLiveMode ? liveRobotHeading : 0,
                            }}
                        />
                    )}
                </GoogleMap>
            </div>
        </div>
    );
}

export default FieldRowsMap;




